import type { ApiResponse } from '@gokrew/models/generic';
import type { MessagingApi } from '@gokrew/ui';
import type {
  MessagingBootstrap,
  MessagingConversation,
  MessagingMessage,
  MessagingPage,
  MessagingPerson,
  MessagingSettings,
  MessagingSyncResult,
  MessagingUnreadResult,
  ReadMessagingConversationResult,
  SendMessagingMessageResult,
} from '@gokrew/models/messaging';
import client from './client';

const base = '/messaging';
const endpoints = {
  bootstrap: `${base}/bootstrap`,
  settings: `${base}/settings`,
  unread: `${base}/unread`,
  recipients: `${base}/recipients`,
  conversations: `${base}/conversations`,
  sync: `${base}/sync`,
  conversation: (id: string) => `${base}/conversations/${id}`,
  messages: (id: string) => `${base}/conversations/${id}/messages`,
  read: (id: string) => `${base}/conversations/${id}/read`,
};

export const messagingSocketUrl = () =>
  `${new URL(client.defaults.baseURL ?? '/api/v1', window.location.origin).origin}/messaging`;

export const createMessagingApi = (companyId: string): MessagingApi => {
  const params = { companyId };
  return {
    bootstrap: async () =>
      (
        await client.get<ApiResponse<MessagingBootstrap>>(endpoints.bootstrap, {
          params,
        })
      ).data.data,
    settings: async () =>
      (
        await client.get<ApiResponse<MessagingSettings>>(endpoints.settings, {
          params,
        })
      ).data.data,
    updateSettings: async (payload) =>
      (
        await client.put<ApiResponse<MessagingSettings>>(
          endpoints.settings,
          payload,
          { params }
        )
      ).data.data,
    unread: async () =>
      (
        await client.get<ApiResponse<MessagingUnreadResult>>(endpoints.unread, {
          params,
        })
      ).data.data,
    recipients: async (query) =>
      (
        await client.get<ApiResponse<MessagingPage<MessagingPerson>>>(
          endpoints.recipients,
          { params: { ...query, ...params } }
        )
      ).data.data,
    conversations: async (query) =>
      (
        await client.get<ApiResponse<MessagingPage<MessagingConversation>>>(
          endpoints.conversations,
          { params: { ...query, ...params } }
        )
      ).data.data,
    conversation: async (id) =>
      (
        await client.get<ApiResponse<MessagingConversation>>(
          endpoints.conversation(id),
          { params }
        )
      ).data.data,
    createConversation: async (recipientUserId) =>
      (
        await client.post<ApiResponse<MessagingConversation>>(
          endpoints.conversations,
          { recipientUserId },
          { params }
        )
      ).data.data,
    messages: async (id, query) =>
      (
        await client.get<ApiResponse<MessagingPage<MessagingMessage>>>(
          endpoints.messages(id),
          { params: { ...query, ...params } }
        )
      ).data.data,
    send: async (id, payload) =>
      (
        await client.post<ApiResponse<SendMessagingMessageResult>>(
          endpoints.messages(id),
          payload,
          { params }
        )
      ).data.data,
    read: async (id, throughSequence) =>
      (
        await client.post<ApiResponse<ReadMessagingConversationResult>>(
          endpoints.read(id),
          { throughSequence },
          { params }
        )
      ).data.data,
    sync: async (after) =>
      (
        await client.get<ApiResponse<MessagingSyncResult>>(endpoints.sync, {
          params: { after, limit: 100, ...params },
        })
      ).data.data,
  };
};
